import { ArrowLeft } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/componentes/ui/button';
import { etapaAnterior, proximaEtapa, type IdEtapa } from '@/etapas';
import { cn } from '@/lib/utils';

/* RodapeFunil — a barra que fecha uma etapa: voltar à esquerda, avançar à
 * direita, e no meio o nome de para onde se está indo.
 *
 * Três decisões, e as três vieram de ver alguém usando:
 *
 * 1. A BARRA FICA NO FIM DA ETAPA e rola junto com ela (a classe
 *    `.barra-acao`, em globals.css). Já foi sticky, colada no rodapé da
 *    janela; saiu por decisão do dono. O motivo está escrito junto da classe.
 *
 * 2. O VOLTAR DIZ O NOME DA ETAPA ANTERIOR, não "Voltar". Num funil de sete
 *    etapas, "voltar" é a pergunta, não a resposta.
 *
 * 3. A PRÓXIMA ETAPA É ANUNCIADA antes de a pessoa clicar. "Continuar" sem
 *    destino obriga a clicar para descobrir para onde vai, e clicar para
 *    descobrir é o oposto de conduzir.
 *
 * Só a navegação é igual em toda etapa; avançar muda (link, submit, salvar
 * antes de seguir), então a ação principal chega como children em vez de virar
 * cinco props booleanas. */
export function RodapeFunil({
  etapa,
  voltar,
  rotuloVoltar,
  className,
  children,
}: {
  /** A etapa em que a barra está. Anterior e próxima saem de `src/etapas.ts`. */
  etapa: IdEtapa;
  /** Sobrescreve o destino do voltar. Use quando a tela foi aberta de fora do funil. */
  voltar?: string | undefined;
  rotuloVoltar?: string | undefined;
  /** Só para posicionar a barra na grade da etapa (ex.: atravessar duas colunas). */
  className?: string | undefined;
  children: React.ReactNode;
}) {
  const anterior = etapaAnterior(etapa);
  const proxima = proximaEtapa(etapa);

  const destino = voltar ?? anterior?.rota;
  const rotulo = rotuloVoltar ?? anterior?.nome ?? 'Voltar';

  return (
    <div className={cn('barra-acao', className)} data-nao-imprimir="">
      {destino ? (
        <Button variant="outline" asChild>
          <Link to={destino}>
            <ArrowLeft />
            {rotulo}
          </Link>
        </Button>
      ) : (
        <span />
      )}

      <div className="flex items-center gap-4">
        {/* Some no celular: ali a barra tem largura para os dois botões e mais
            nada, e espremer o texto entre eles faria os dois encolherem. */}
        {proxima ? (
          <span className="text-tinta-2 hidden text-nota mesa:block">
            A seguir, {proxima.nome.toLowerCase()}
          </span>
        ) : null}
        {children}
      </div>
    </div>
  );
}
